import { Row, Col, Card, Statistic, Spin, Alert } from 'antd';
import {
  ShoppingOutlined,
  DollarOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  RiseOutlined
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { dashboardApi } from '../api/dashboard';
import { OrderChart } from '../components/Charts/OrderChart';
import { RevenueChart } from '../components/Charts/RevenueChart';
import { formatCurrency } from '../utils/formatters';

export const Dashboard: React.FC = () => {
  const { data: stats, isLoading, error } = useQuery({
    queryKey: ['dashboard'],
    queryFn: dashboardApi.getStats,
    refetchInterval: 30000 // Refresh every 30 seconds
  });

  const { data: chartData } = useQuery({
    queryKey: ['dashboard-chart'],
    queryFn: () => dashboardApi.getChartData(7),
    refetchInterval: 60000
  });

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Помилка завантаження"
        description={(error as Error).message}
        type="error"
        showIcon
      />
    );
  }

  return (
    <div>
      <h1>Головна панель</h1>

      {/* KPI Cards */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={8}>
          <Card>
            <Statistic
              title="Нові замовлення"
              value={stats?.orders.new || 0}
              valueStyle={{ color: '#cf1322' }}
              prefix={<ClockCircleOutlined />}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={8}>
          <Card>
            <Statistic
              title="В роботі"
              value={stats?.orders.in_progress || 0}
              valueStyle={{ color: '#faad14' }}
              prefix={<ShoppingOutlined />}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={8}>
          <Card>
            <Statistic
              title="Завершено сьогодні"
              value={stats?.orders.completed_today || 0}
              valueStyle={{ color: '#3f8600' }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={8}>
          <Card>
            <Statistic
              title="Виручка сьогодні"
              value={formatCurrency(stats?.revenue.today || 0)}
              valueStyle={{ color: '#3f8600' }}
              prefix={<DollarOutlined />}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={8}>
          <Card>
            <Statistic
              title="Виручка за тиждень"
              value={formatCurrency(stats?.revenue.this_week || 0)}
              valueStyle={{ color: '#1890ff' }}
              prefix={<RiseOutlined />}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={8}>
          <Card>
            <Statistic
              title="Критичні помилки (DLQ)"
              value={stats?.dlq_count || 0}
              valueStyle={{ color: stats?.dlq_count ? '#cf1322' : '#3f8600' }}
              prefix={<WarningOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Charts */}
      <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
        <Col xs={24} lg={12}>
          <Card title="Замовлення за останні 7 днів">
            {chartData ? (
              <OrderChart data={chartData} />
            ) : (
              <Spin />
            )}
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="Виручка за останні 7 днів">
            {chartData ? (
              <RevenueChart data={chartData} />
            ) : (
              <Spin />
            )}
          </Card>
        </Col>
      </Row>

      {/* System Health */}
      <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
        <Col span={24}>
          <Card title="Стан системи">
            <Row gutter={16}>
              <Col span={6}>
                <Statistic
                  title="API"
                  value={stats?.system_health.api_status || 'unknown'}
                  valueStyle={{
                    color: stats?.system_health.api_status === 'healthy' ? '#3f8600' : '#cf1322'
                  }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Celery"
                  value={stats?.system_health.celery_status || 'unknown'}
                  valueStyle={{
                    color: stats?.system_health.celery_status === 'healthy' ? '#3f8600' : '#cf1322'
                  }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Redis"
                  value={stats?.system_health.redis_status || 'unknown'}
                  valueStyle={{
                    color: stats?.system_health.redis_status === 'healthy' ? '#3f8600' : '#cf1322'
                  }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Database"
                  value={stats?.system_health.db_status || 'unknown'}
                  valueStyle={{
                    color: stats?.system_health.db_status === 'healthy' ? '#3f8600' : '#cf1322'
                  }}
                />
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
    </div>
  );
};
